import type { ReviewCycle, ReviewerContext, ReviewerRef } from './types';

/**
 * Sequence-stage helpers — 2026-08-20 (Fredrik).
 *
 * One definition of "which step is this cycle at" and "is that step mine",
 * shared by ReviewSequenceIndicator, ReviewDashboard and ReviewInterface.
 *
 * Written because the dashboard had NO notion of the sequence at all: its
 * reviewer status asked only "did I submit?", so a reviewer sitting third in
 * the sequence saw "Needs Your Review" on a cycle they could not act on —
 * and at the time they couldn't take the (since-removed) edit lock either. The reviewer
 * had no way to tell "you are holding this up" from "you are waiting on
 * someone else".
 *
 * Kept as one module rather than reimplemented per call site because the
 * "is this my step" rule is NOT uniform across steps (see isYourStep), and a
 * second copy that missed that would put a YOU pill on a colleague's step —
 * which is precisely the bug walk fix F11 (2026-08-12) fixed once already.
 */

export type SequenceStep = 'medical' | 'legal' | 'producer';

export const STEP_LABEL: Record<SequenceStep, string> = {
  medical: 'Medical',
  legal: 'Legal',
  producer: 'Producer'
};

/**
 * The next uncompleted step in the cycle's sequence, or null when the cycle
 * has no sequence (legacy cycles, and any cycle whose reviewers had no
 * ReviewerContext when the sequence was derived — deriveReviewSequence
 * resolves zero domains and returns null, so this is not rare).
 */
export function currentStep(cycle: ReviewCycle): SequenceStep | null {
  const seq = cycle.reviewSequence || [];
  if (seq.length === 0) return null;
  const sorted = [...seq].sort((a, b) => a.order - b.order);
  const completed = new Set((cycle.stepCompletions || []).map(sc => sc.step));
  const next = sorted.find(s => !completed.has(s.step));
  return (next?.step as SequenceStep) || null;
}

/**
 * Whether `step` belongs to the viewer.
 *
 * Walk fix F11 (2026-08-12), preserved here verbatim in behaviour: the
 * producer step belongs to a NAMED person (cycle.producer), so it matches on
 * email — because useCurrentUserStep resolves DMs and admins WITHOUT a
 * ReviewerContext to 'producer' as an edit-lock fallback, which made every
 * admin viewing a cycle see "YOU" on a step owned by a named colleague.
 * Medical and legal only ever resolve through a real ReviewerContext, so
 * they are already person-derived and match on the step itself.
 */
export function isYourStep(
  cycle: ReviewCycle,
  step: SequenceStep | null,
  currentUserStep: SequenceStep | null,
  userEmail?: string | null
): boolean {
  if (!step) return false;
  if (step === 'producer') {
    const producerEmail = (cycle.producer?.email || '').toLowerCase();
    const viewerEmail = (userEmail || '').toLowerCase();
    return !!viewerEmail && viewerEmail === producerEmail;
  }
  return currentUserStep === step;
}

/**
 * Is the viewer the person named as this cycle's producer?
 *
 * Split out of ReviewInterface (2026-08-31, Fredrik — Johanna is both the
 * medical reviewer and the producer on the Explainer cycle) because the
 * interface had it as `readOnlyParticipant && isNamedProducer`, which
 * conflates two unrelated facts: WHO you are on this cycle, and HOW MUCH of
 * it you may see. A producer who is also a full-access reviewer is not a
 * read-only participant, so the read-back panel never mounted for her — while
 * the sequence indicator badged the producer step YOU (isYourStep matches it
 * by email) and the producer dashboard filed the cycle under Action Required.
 * The app routed her to a page with no control on it.
 *
 * Producer identity is per-cycle and person-derived, exactly as isYourStep
 * treats it; read-only-ness stays on `readOnlyParticipant` where it belongs.
 */
export function isNamedProducer(
  cycle: ReviewCycle,
  userEmail?: string | null
): boolean {
  const viewer = (userEmail || '').toLowerCase();
  return !!viewer && (cycle.producer?.email || '').toLowerCase() === viewer;
}

/**
 * The REVIEWER step the viewer may hand over right now — the next step in the
 * sequence, when it is medical or legal and it belongs to them. Null otherwise.
 *
 * Two callers, deliberately one rule: the assessment form's auto-complete
 * ("Complete Review" IS the hand-over, 2026-08-20) and MarkStepCompleteButton.
 * They used to disagree — the button compared `nextStep.step` to
 * `currentUserStep` raw, which is the pre-F11 comparison, so an admin (whom
 * useCurrentUserStep resolves to 'producer' as an edit-lock fallback) was shown
 * the producer's own green "Mark ready to distribute" on every cycle sitting at
 * that step, indistinguishable from "Complete Review" beside it and completing
 * a named colleague's step with nothing saying so.
 *
 * NEVER the producer step, for two separate reasons:
 *   - it carries a commercial read-back neither caller collects, and
 *     POST /steps/producer/complete refuses without one (READBACK_REQUIRED);
 *   - it has ONE home — ProducerReadbackPanel — and a second control for it
 *     is how the two got out of step in the first place.
 *
 * Framed as "the next step, if it is yours" rather than "your step, if it is
 * next": a reviewer who is also the named producer answers isYourStep TRUE on
 * the producer step, and the old framing would have labelled that button from
 * her `currentUserStep` ('medical') while completing something else.
 */
export function reviewerHandoverStep(
  cycle: ReviewCycle,
  currentUserStep: SequenceStep | null,
  userEmail?: string | null
): SequenceStep | null {
  const step = currentStep(cycle);
  if (!step || step === 'producer') return null;
  return isYourStep(cycle, step, currentUserStep, userEmail) ? step : null;
}

/**
 * Assigned reviewers who contribute NO step to the sequence — 2026-08-31
 * (Fredrik, asking why a cycle with an assigned legal reviewer had no legal
 * step; in prod only three people had a ReviewerContext at all).
 *
 * `content_review.reviewer` says you MAY review; a ReviewerContext with a
 * domain says WHICH STEP you are; deriveReviewSequence reads only the second.
 * A reviewer with the bit and no context can comment and assess, but the
 * sequence — and therefore the STEPS_NOT_COMPLETE approve gate — never waits
 * on them. That was silent; this makes it printable.
 */
export function reviewersWithoutStep(
  cycle: ReviewCycle,
  contexts: ReviewerContext[]
): ReviewerRef[] {
  return (cycle.assignedReviewers || []).filter(r =>
    !contexts.some(c =>
      String(c.reviewer?._id || '') === String(r._id || '') ||
      (c.reviewer?.email || '').toLowerCase() === (r.email || '').toLowerCase()
    ));
}

/**
 * Is the viewer one of this cycle's OWN reviewers — someone whose assessment
 * form should render at all?
 *
 * Both sources, deliberately: prescreen stubs an entry into
 * reviewerAssessments for every assigned reviewer, but cycles predating that
 * carry only assignedReviewers. Stand-in entries do not count — a decision
 * maker who stood in files through the DM view, and rendering the reviewer
 * form for them was how an unsubmittable form ended up in front of one.
 */
export function isAssignedReviewer(
  cycle: ReviewCycle,
  userEmail?: string | null
): boolean {
  const e = (userEmail || '').toLowerCase();
  if (!e) return false;
  if ((cycle.assignedReviewers || []).some(r => (r.email || '').toLowerCase() === e)) return true;
  return (cycle.reviewerAssessments || []).some(a =>
    (a.reviewer?.email || '').toLowerCase() === e && !a.standsInFor?.step);
}

/** What a step's owner is called, and what completing it on their behalf fails
 *  to record. Both are shown to the decision maker before they click. */
export interface OpenStepOnBehalf {
  step: SequenceStep;
  /** Named for the producer step (cycle.producer); the reviewer steps are
   *  assigned per-domain rather than per-person on the cycle, so they read as
   *  the domain. */
  owner: string;
  /** The input the sequence expected and will not get. */
  missing: string;
}

/**
 * The open sequence steps a decision maker may complete for someone else.
 *
 * The backend has allowed this all along — ASSESSED_STEPS exempts
 * `content_review.decision_maker` for medical/legal, READBACK_REQUIRED exempts
 * it for producer — but only the producer step ever had a control, and that one
 * arrived 2026-08-31. Approval is now gated on the sequence being finished
 * (STEPS_NOT_COMPLETE), so without this the gate would be a dead end rather
 * than a safeguard: a DM facing an unavailable reviewer could neither approve
 * nor advance. The two ship together on purpose.
 *
 * Only the NEXT open step is offered, not every open one — the sequence is
 * ordered, and completing a later step first would let the DM skip past a
 * reviewer who is still working rather than standing in for one who is gone.
 */
export function openStepsOnBehalf(
  cycle: ReviewCycle,
  isDecisionMaker: boolean,
  userEmail?: string | null
): OpenStepOnBehalf[] {
  if (!isDecisionMaker) return [];
  if (['approved', 'rejected', 'canceled', 'superseded'].includes(cycle.status as string)) return [];
  const step = currentStep(cycle);
  if (!step) return [];
  // Never offer a step that is the viewer's own to take: they act as
  // themselves, which records the input this path cannot.
  if (isYourStep(cycle, step, null, userEmail) && step === 'producer') return [];
  if (step === 'producer') {
    return [{
      step,
      owner: cycle.producer?.name || 'the producer',
      missing: 'their commercial perspective'
    }];
  }
  return [{
    step,
    owner: `${STEP_LABEL[step]} review`,
    missing: 'a risk assessment from that reviewer'
  }];
}

/**
 * What this cycle means FOR THIS REVIEWER, as one word the dashboard can
 * group and badge by.
 *
 *   your_turn  — the sequence is at your step; you are what it is waiting on
 *   waiting    — assigned, but the sequence is at someone else's step
 *   submitted  — you have submitted your assessment
 *   approved / revision_needed / rejected — terminal, nothing to do
 *
 * `waiting` collapses to `your_turn` on cycles with no sequence: with nothing
 * to order the reviewers, everyone is free to act, and telling a reviewer to
 * wait for a step that does not exist would strand the cycle.
 */
export type ReviewerStage =
  | 'your_turn' | 'waiting' | 'submitted'
  | 'approved' | 'revision_needed' | 'rejected';

export function reviewerStage(
  cycle: ReviewCycle,
  userEmail: string | null | undefined,
  currentUserStep: SequenceStep | null
): ReviewerStage {
  if (cycle.status === 'approved' || cycle.status === 'revision_needed' || cycle.status === 'rejected') {
    return cycle.status as ReviewerStage;
  }
  const step = currentStep(cycle);

  // The producer turn out-ranks "you already submitted" — 2026-08-31.
  //
  // 'submitted' means "your part is done", and for medical and legal it is.
  // For someone who is ALSO the named producer it was a lie: her medical
  // assessment was in, so her dashboard filed the cycle as finished while the
  // sequence was blocked waiting on her read-back. Narrowed to the producer
  // step deliberately — a medical or legal reviewer who submitted still reads
  // as 'submitted', which is unchanged and correct.
  if (step === 'producer' && isYourStep(cycle, step, currentUserStep, userEmail)) {
    return 'your_turn';
  }

  const mine = cycle.reviewerAssessments?.find(
    a => (a.reviewer?.email || '').toLowerCase() === (userEmail || '').toLowerCase()
  );
  if (mine?.submittedAt) return 'submitted';

  if (!step) return 'your_turn';
  return isYourStep(cycle, step, currentUserStep, userEmail) ? 'your_turn' : 'waiting';
}

/** "Waiting on Legal" — what the cycle is blocked on, for a `waiting` row. */
export function waitingOnLabel(cycle: ReviewCycle): string | null {
  const step = currentStep(cycle);
  return step ? STEP_LABEL[step] : null;
}
